import React, { useState, useEffect, useRef } from "react";
import { useForm, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";

import useSkeleton from "../../../../hooks/useSkeleton";

import { loadingState } from "../../../../states/loadingState";

import { InputText } from "primereact/inputtext";
import { Password } from "primereact/password";
import { Checkbox } from "primereact/checkbox";
import { Button } from "primereact/button";
import { classNames } from "primereact/utils";
import useCRUDService from "../../../../hooks/useCRUDService";
import "react-toastify/dist/ReactToastify.css";
import { Toast } from "primereact/toast";
import Layout from "../../../../layout/layout";

const BeneficiariesForm = () => {
  const CRUDService = useCRUDService("beneficiaries");
  const s = useSkeleton("abm");
  const { t } = useTranslation();
  const toast = useRef(null);
  const {
    unregister,
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: "",
      lastName: "",
      documentType: 1,
      documentNumber: "",
      phone: "",
      address: "",
      notes: "",
      active: 0,
    },
  });

  const [isLoading, setIsLoading] = useAtom(loadingState);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await CRUDService.getEntity(
          localStorage.getItem("userId")
        );

        for (const key in data) {
          setValue(key, data[key]);
        }
      } catch (error) {
        console.error(t("forms.messages.operation.error"), error);
      }
    };

    fetchData();
  }, [setValue]);

  const handleShow = (e) => {
    e.preventDefault();
    if (isVisible) {
      unregister("password");
    }
    setIsVisible(!isVisible);
  };

  const handleHide = () => {
    setIsVisible(false);
  };

  const onSubmit = async (data) => {
    setIsLoading(true);

    try {
      await CRUDService.updateEntity(data);

      toast.current.show({
        severity: "success",
        summary: t("forms.messages.titles.success"),
        detail: t("forms.messages.operation.success"),
        life: 3000,
      });
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: t("forms.messages.titles.error"),
        detail: t("forms.messages.operation.error"),
        life: 3000,
      });
    }
    setIsLoading(false);
  };

  const getFormErrorMessage = (error) => {
    return error && <small className="p-error">{error.message}</small>;
  };
  return (
    <React.Fragment>
      <Layout>
        <Toast ref={toast} />
        <div className="flex flex-grow-1 flex-column w-screen h-full">
          <div className="flex flex-column">
            <div className="flex justify-content-center">
              <h2 className="pageTitle">Datos personales</h2>
            </div>
            <div className="flex justify-content-center">
              <p>
                Mantené actualizado tus datos para recibir información al
                instante de nuestros servicios.
              </p>
            </div>
          </div>
          <div className="flex justify-content-center w-full ">
            <div className="flex w-6">
              <form onSubmit={handleSubmit(onSubmit)} className={s.get("form")}>
                <Controller
                  name="name"
                  control={control}
                  rules={{
                    required: t("forms.validation.required", {
                      entity: t("entities.beneficiaries.name"),
                    }),
                    maxLength: {
                      value: 50,
                      message: t("forms.validation.maxLength", {
                        entity: t("entities.beneficiaries.name"),
                        maxLength: 50,
                      }),
                    },
                  }}
                  render={({ field, fieldState }) => (
                    <div className={s.get("form.field")}>
                      <div className={s.get("form.field.label")}>
                        <label htmlFor="name">
                          {t("entities.beneficiaries.name")}
                        </label>
                      </div>
                      <div className={s.get("form.field.value")}>
                        <div className={s.get("form.field.value.error")}>
                          {getFormErrorMessage(errors.name)}
                        </div>
                        <div className={s.get("form.field.value.control")}>
                          <InputText
                            id="name"
                            {...field}
                            value={field.value ?? ""}
                            maxLength={100}
                            className={[
                              "form-control",
                              classNames({
                                "p-invalid": fieldState.invalid,
                              }),
                            ]}
                            disabled
                          />
                        </div>
                      </div>
                    </div>
                  )}
                />
                <Controller
                  name="lastName"
                  control={control}
                  rules={{
                    required: t("forms.validation.required", {
                      entity: t("entities.beneficiaries.lastName"),
                    }),
                    maxLength: {
                      value: 50,
                      message: t("forms.validation.maxLength", {
                        entity: t("entities.beneficiaries.lastName"),
                        maxLength: 50,
                      }),
                    },
                  }}
                  render={({ field, fieldState }) => (
                    <div className={s.get("form.field")}>
                      <div className={s.get("form.field.label")}>
                        <label htmlFor="lastName">
                          {t("entities.beneficiaries.lastName")}
                        </label>
                      </div>
                      <div className={s.get("form.field.value")}>
                        <div className={s.get("form.field.value.error")}>
                          {getFormErrorMessage(errors.lastName)}
                        </div>
                        <div className={s.get("form.field.value.control")}>
                          <InputText
                            id="lastName"
                            {...field}
                            value={field.value ?? ""}
                            maxLength={100}
                            className={[
                              "form-control",
                              classNames({
                                "p-invalid": fieldState.invalid,
                              }),
                            ]}
                            disabled
                          />
                        </div>
                      </div>
                    </div>
                  )}
                />
                <Controller
                  name="documentNumber"
                  control={control}
                  rules={{
                    required: t("forms.validation.required", {
                      entity: t("entities.beneficiaries.documentNumber"),
                    }),
                    maxLength: {
                      value: 50,
                      message: t("forms.validation.maxLength", {
                        entity: t("entities.beneficiaries.documentNumber"),
                        maxLength: 50,
                      }),
                    },
                  }}
                  render={({ field, fieldState }) => (
                    <div className={s.get("form.field")}>
                      <div className={s.get("form.field.label")}>
                        <label htmlFor="documentNumber">
                          {t("entities.beneficiaries.documentNumber")}
                        </label>
                      </div>
                      <div className={s.get("form.field.value")}>
                        <div className={s.get("form.field.value.error")}>
                          {getFormErrorMessage(errors.documentNumber)}
                        </div>
                        <div className={s.get("form.field.value.control")}>
                          <InputText
                            id="documentNumber"
                            {...field}
                            value={field.value ?? ""}
                            maxLength={100}
                            className={[
                              "form-control",
                              classNames({
                                "p-invalid": fieldState.invalid,
                              }),
                            ]}
                            disabled
                          />
                        </div>
                      </div>
                    </div>
                  )}
                />
                <Controller
                  name="phone"
                  control={control}
                  rules={{
                    maxLength: {
                      value: 50,
                      message: t("forms.validation.maxLength", {
                        entity: t("entities.beneficiaries.phone"),
                        maxLength: 50,
                      }),
                    },
                  }}
                  render={({ field, fieldState }) => (
                    <div className={s.get("form.field")}>
                      <div className={s.get("form.field.label")}>
                        <label htmlFor="phone">
                          {t("entities.beneficiaries.phone")}
                        </label>
                      </div>
                      <div className={s.get("form.field.value")}>
                        <div className={s.get("form.field.value.error")}>
                          {getFormErrorMessage(errors.phone)}
                        </div>
                        <div className={s.get("form.field.value.control")}>
                          <InputText
                            id="phone"
                            {...field}
                            value={field.value ?? ""}
                            maxLength={100}
                            className={[
                              "form-control",
                              classNames({
                                "p-invalid": fieldState.invalid,
                              }),
                            ]}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                />
                <Controller
                  name="address"
                  control={control}
                  rules={{
                    required: t("forms.validation.required", {
                      entity: t("entities.beneficiaries.address"),
                    }),
                    maxLength: {
                      value: 500,
                      message: t("forms.validation.maxLength", {
                        entity: t("entities.beneficiaries.address"),
                        maxLength: 500,
                      }),
                    },
                  }}
                  render={({ field, fieldState }) => (
                    <div className={s.get("form.field")}>
                      <div className={s.get("form.field.label")}>
                        <label htmlFor="address">
                          {t("entities.beneficiaries.address")}
                        </label>
                      </div>
                      <div className={s.get("form.field.value")}>
                        <div className={s.get("form.field.value.error")}>
                          {getFormErrorMessage(errors.address)}
                        </div>
                        <div className={s.get("form.field.value.control")}>
                          <InputText
                            id="address"
                            {...field}
                            value={field.value ?? ""}
                            maxLength={100}
                            className={[
                              "form-control",
                              classNames({
                                "p-invalid": fieldState.invalid,
                              }),
                            ]}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                />

                <Controller
                  name="email"
                  control={control}
                  rules={{
                    pattern: {
                      value: /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/,
                      message: t("forms.validation.noValidEmail"),
                    },
                    maxLength: {
                      value: 60,
                      message: t("forms.validation.maxLength", {
                        entity: t("entities.beneficiaries.email"),
                        maxLength: 60,
                      }),
                    },
                  }}
                  render={({ field, fieldState }) => (
                    <div className={s.get("form.field")}>
                      <div className={s.get("form.field.label")}>
                        <label htmlFor="email">
                          {t("entities.beneficiaries.email")}
                        </label>
                      </div>
                      <div className={s.get("form.field.value")}>
                        <div className={s.get("form.field.value.error")}>
                          {getFormErrorMessage(errors.email)}
                        </div>
                        <div className={s.get("form.field.value.control")}>
                          <InputText
                            id="email"
                            {...field}
                            value={field.value || ""}
                            maxLength={60}
                            className={[
                              "form-control",
                              classNames({
                                "p-invalid": fieldState.invalid,
                              }),
                            ]}
                            autoComplete="off"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                />

                <Controller
                  name="nick"
                  control={control}
                  rules={{
                    required: t("forms.validation.required", {
                      entity: t("entities.beneficiaries.nick"),
                    }),
                    maxLength: {
                      value: 500,
                      message: t("forms.validation.maxLength", {
                        entity: t("entities.beneficiaries.nick"),
                        maxLength: 500,
                      }),
                    },
                  }}
                  render={({ field, fieldState }) => (
                    <div className={s.get("form.field")}>
                      <div className={s.get("form.field.label")}>
                        <label htmlFor="nick">
                          {t("entities.beneficiaries.nick")}
                        </label>
                      </div>
                      <div className={s.get("form.field.value")}>
                        <div className={s.get("form.field.value.error")}>
                          {getFormErrorMessage(errors.nick)}
                        </div>
                        <div className={s.get("form.field.value.control")}>
                          <InputText
                            id="nick"
                            {...field}
                            value={field.value ?? ""}
                            maxLength={100}
                            className={[
                              "form-control",
                              classNames({
                                "p-invalid": fieldState.invalid,
                              }),
                            ]}
                            disabled
                          />
                        </div>
                      </div>
                    </div>
                  )}
                />

                <Controller
                  name="password"
                  control={control}
                  rules={
                    isVisible
                      ? {
                        required: t("forms.validation.required", {
                          entity: t("entities.beneficiaries.password"),
                        }),
                        maxLength: {
                          value: 500,
                          message: t("forms.validation.maxLength", {
                            entity: t("entities.beneficiaries.password"),
                            maxLength: 500,
                          }),
                        },
                      }
                      : ""
                  }
                  render={({ field, fieldState }) => (
                    <div className={s.get("form.field")}>
                      <div className={s.get("form.field.label")}>
                        <label htmlFor="password">
                          {t("entities.beneficiaries.password")}
                        </label>
                      </div>
                      <div className={s.get("form.field.value")}>
                        <div>
                          <div className={s.get("form.field.value.error")}>
                            {getFormErrorMessage(errors.password)}
                          </div>
                          <div className="flex flex-row align-items-center">
                            {isVisible && (
                              <div>
                                <Password
                                  id="password"
                                  {...field}
                                  inputRef={field.ref}
                                  className={[
                                    "form-control",
                                    classNames({
                                      "p-invalid": fieldState.invalid,
                                    }),
                                  ]}
                                  maxLength={100}
                                  feedback={false}
                                  autoComplete="new-password"
                                />
                              </div>
                            )}

                            <div>
                              <Button
                                style={{
                                  boxShadow: "none",
                                  margin: "0",
                                }}
                                label={
                                  isVisible
                                    ? t("entities.beneficiaries.hidePassword")
                                    : t("entities.beneficiaries.showPassword")
                                }
                                onClick={handleShow}
                                link
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                />

                <div className="flex justify-content-end mt-6 mb-2">
                  <Button
                    label={
                      isLoading
                        ? `${t("buttons.updating")}...`
                        : t("buttons.update")
                    }
                    icon="pi pi-check"
                    className=" btn btn-primary btn-primary-crud"
                    type="submit"
                    disabled={isLoading}
                  />
                </div>
              </form>
            </div>
          </div>
        </div>
      </Layout>
    </React.Fragment>
  );
};

export default BeneficiariesForm;
